import { SMSTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Thread as V4Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { CallResult, EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { InboxTag, ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Thread as UnifiedThread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { SMSItem, CallRecordItem, AiActivityItem } from '../types';

export function isSmsMessageItem(item: ThreadItem): item is SMSItem {
  return 'smsMessage' in item && item.smsMessage !== undefined;
}

export function isCallRecordItem(item: ThreadItem): item is CallRecordItem {
  return 'callRecord' in item && item.callRecord !== undefined;
}

export function isCallRecordVoicemailItem(item: ThreadItem): item is CallRecordItem {
  return isCallRecordItem(item) && item.callRecord?.callResult === CallResult.CALL_RESULT_VOICEMAIL;
}

export function isAiActivityItem(item: ThreadItem): item is AiActivityItem {
  return 'aiActivity' in item && item.aiActivity !== undefined;
}

export function getThreadSMSItems(items: ThreadItem[]) {
  return items.filter(isSmsMessageItem).map((item) => item.smsMessage);
}

export function getThreadItemId(item?: ThreadItem): string {
  if (!item) return '';
  if ('smsMessage' in item) {
    return item.smsMessage.id;
  } else if ('callRecord' in item) {
    return item.callRecord.threadCallId;
  } else if ('aiActivity' in item) {
    return item.aiActivity.relatedSmsId ?? '';
  }
  return '';
}

export function getThreadItemCreatedAt(item?: ThreadItem): string {
  if (!item) return '';
  if ('smsMessage' in item) {
    return item.smsMessage.createdAt;
  } else if ('callRecord' in item) {
    return item.callRecord.startedAt ?? '';
  } else if ('aiActivity' in item) {
    return item.aiActivity.createdAt ?? '';
  }
  return '';
}

export function getThreadIdFromThreadItem(item?: ThreadItem): string {
  if (!item) return '';
  if ('smsMessage' in item) {
    return item.smsMessage.threadId;
  } else if ('callRecord' in item) {
    return item.callRecord.threadId;
  } else if ('aiActivity' in item) {
    return item.aiActivity.threadId ?? '';
  }
  return '';
}

export function getThreadPersonPhone(item?: ThreadItem): string {
  if (!item) return '';
  if ('smsMessage' in item) {
    return item.smsMessage.personPhone;
  } else if ('callRecord' in item) {
    return item.callRecord.personPhone;
  } else if ('aiActivity' in item) {
    return item.aiActivity.personPhoneNumber ?? '';
  }
  return '';
}

export function getThreadPersonId(item?: ThreadItem): string {
  if (!item) return '';
  if ('smsMessage' in item) {
    return item.smsMessage.personId;
  } else if ('callRecord' in item) {
    return item.callRecord.personId ?? '';
  } else if ('aiActivity' in item) {
    return 'aiActivity';
  }
  return '';
}

export function getThreadLocationPhone(item?: ThreadItem): string {
  if (!item) return '';
  if ('smsMessage' in item) {
    return item.smsMessage.locationPhone;
  } else if ('callRecord' in item) {
    return item.callRecord.locationPhone ?? '';
  } else if ('aiActivity' in item) {
    return item.aiActivity.locationPhoneNumber ?? '';
  }
  return '';
}

/**
 * Converts an SMSTag (from v1 schema) to an InboxTag array (v2 schema).
 * Used when transitioning between v3/v4 SMS mutations and unified thread updates.
 *
 * @param smsTag - The SMSTag from the mutation response (v1 schema)
 * @returns An array containing the converted InboxTag (v2 schema)
 */
export const convertSmsTagToInboxTag = (smsTag: SMSTag): InboxTag[] => {
  // If the SMSTag doesn't have required fields, return empty array
  if (!smsTag.smsId || !smsTag.tagId) {
    return [];
  }

  const inboxTag: InboxTag = {
    entityId: smsTag.smsId,
    tagId: smsTag.tagId,
    entityCreatedAt: smsTag.smsCreatedAt || '',
    appliedBy: smsTag.appliedBy || '',
    entityType: EntityType.ENTITY_TYPE_SMS,
  };

  return [inboxTag];
};

// Type guard functions
export const isV4Thread = (thread: UnifiedThread | V4Thread): thread is V4Thread =>
  thread && typeof thread === 'object' && 'messages' in thread && Array.isArray(thread.messages);

export const isUnifiedThread = (thread: UnifiedThread | V4Thread): thread is UnifiedThread =>
  thread && typeof thread === 'object' && 'items' in thread && Array.isArray(thread.items);
